import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import DashboardTopbar from "../../components/DashboardTopbar/DashboardTopbar";
import KPICard from "../../components/shared/KPICard/KPICard";
import localeText from "../../../../locale";
import useCurrency from "../../../../hooks/useCurrency";
import { formatPrice } from "../../../../utilities/formatPrice";
import TransferExtracreditCard from "../FinancialHub/components/TransferExtracreditCard/TransferExtracreditCard";
import QuickAccessCard from "./components/QuickAccessCard";
import CustomerInfoSection from "./components/CustomerInfoSection";
import ExtracreditOpportunities from "../FinancialHub/components/ExtracreditOpportunities/ExtracreditOpportunities";
import ExtracreditMiniStatement from "../FinancialHub/components/ExtracreditMiniStatement/ExtracreditMiniStatement";
import RecentOperations from "../../components/shared/RecentOperations/RecentOperations";
import OrderService from "../../../../service/OrderService";
import QrCodePayDialog from "../../components/QrCodePay/QrCodePayDialog";
import "./CustomerDashboardHome.css";

const orderService = new OrderService();

const CustomerDashboardHome = ({ userInfo, userTypeInfo, currency = "€", extracoinInfo }) => {
  const [pendingTransactions, setPendingTransactions] = useState(0);
  const [qrPayVisible, setQrPayVisible] = useState(false);
  const { currencySymbol } = useCurrency(currency);

  useEffect(() => {
    let cancelled = false;
    const loadPendingTransactions = async () => {
      try {
        const token = localStorage.getItem("__extraconomy_token");
        const username = localStorage.getItem("__extraconomy_username");
        if (!token || !username) return;
        const res = await orderService.getPurchases(token, username);
        if (cancelled) return;
        if (
          (res?.messageCode === 200000 || res?.messageCode === 200001) &&
          Array.isArray(res?.result)
        ) {
          const pending = res.result.filter(
            (order) => order.status !== "RECEIVED" && order.status !== "CANCELED",
          );
          setPendingTransactions(pending.length);
        } else {
          setPendingTransactions(0);
        }
      } catch {
        if (!cancelled) setPendingTransactions(0);
      }
    };
    loadPendingTransactions();
    return () => {
      cancelled = true;
    };
  }, []);

  const extracreditBalance = Number(extracoinInfo?.balance ?? 0) || 0;
  const equivalentInCircuit = useMemo(
    () =>
      localeText("customerHomeExtracreditEquivalent", {
        value: `${currencySymbol} ${formatPrice(extracreditBalance)}`,
      }) || `Equivalente a ~ ${currencySymbol} ${formatPrice(extracreditBalance)} nel circuito`,
    [currencySymbol, extracreditBalance],
  );

  return (
    <DashboardLayout>
      <DashboardTopbar userInfo={userInfo} userTypeInfo={userTypeInfo} />

      <div className="grid mb-3">
        <div className="col-12 md:col-6">
          <KPICard
            title={localeText("customerHomeExtracreditBalanceTitle") || "Saldo Extracredit (ECR)"}
            value={extracreditBalance}
            subtitle={equivalentInCircuit}
            valueLabel="ECR"
            customBgColor="var(--theme-primary-dark, #346958)"
            customTextColor="rgba(255,255,255,0.95)"
            customSubtitleColor="rgba(255,255,255,0.85)"
            currency={currencySymbol}
          />
        </div>
        <div className="col-12 md:col-6">
          <KPICard
            title={localeText("myPurchases") || "Minhas compras"}
            value={pendingTransactions}
            subtitle={
              localeText("customerHomePendingTransactionsSubtitle") || "Visualize seus pedidos"
            }
            valueLabel={
              pendingTransactions === 1 ? localeText("orderSingular") : localeText("orderPlural")
            }
            customerPurchases={true}
          />
        </div>
      </div>

      <div className="mb-4">
        <h3
          className="text-base font-semibold mb-3 mt-0"
          style={{ color: "var(--theme-text, #172B4D)" }}
        >
          {localeText("customerHomeQuickActionsTitle") || "Funzioni rapide"}
        </h3>
        <div className="customer-quick-actions">
          {JSON.parse(localStorage.getItem("__extraconomy_ismobile__") || "false") && (
            <QuickAccessCard
              onClick={() => setQrPayVisible(true)}
              icon="pi pi-qrcode"
              label={localeText("payWithQrCode") || "Paga con QR Code"}
            />
          )}

          <TransferExtracreditCard />
          <QuickAccessCard
            to="/dashboard-mapstores"
            icon="pi pi-compass"
            label={localeText("customerHomeExploreStores") || "Esplora negozi"}
          />
        </div>
      </div>

      {userInfo && (
        <QrCodePayDialog
          visible={qrPayVisible}
          onHide={() => setQrPayVisible(false)}
          user={userInfo}
          currency={currency}
        />
      )}

      {/* <div className="mb-4">
        <CustomerInfoSection />
      </div> */}

      <div className="mb-3">
        <ExtracreditOpportunities
          extracoinPercentage={extracoinInfo?.percent ?? extracoinInfo?.percentage}
          currencySymbol={currencySymbol}
        />
      </div>

      <div className="grid">
        <div className="col-12 lg:col-6">
          <ExtracreditMiniStatement currency={currencySymbol} />
        </div>
        <div className="col-12 lg:col-6">
          <div
            className="card border-round-md overflow-hidden p-3"
            style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
          >
            <RecentOperations isCustomerDashboard={true} currencySymbol={currencySymbol} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerDashboardHome;
